import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { library, type LibraryBook } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { OnboardingEmptyState } from '../components/OnboardingEmptyState';
import { Badge } from '../components/ui';

const COVER_BY_TITLE: Record<string, string> = {
  'The Prince':                  '/covers/prince.jpg',
  "Gulliver's Travels":          '/covers/gulliver.jpg',
  'A Room with a View':          '/covers/room-with-a-view.jpg',
  'Faust':                       '/covers/faust.jpg',
  'Don Quixote':                 '/covers/don-quixote.jpg',
  'The Interpretation of Dreams': '/covers/interpretation.jpg',
};

export default function LibraryPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();

  const FILTERS = useMemo(() => [
    { key: 'all',       label: t('library.filterAll') },
    { key: 'classics',  label: t('library.filterClassics'),   category: 'classics' },
    { key: 'philo',     label: t('library.filterPhilosophy'), category: 'philosophy' },
    { key: 'k5',        label: t('library.filterK5'),         grade: 'K-5' },
    { key: '68',        label: t('library.filter68'),         grade: '6-8' },
    { key: '912',       label: t('library.filter912'),        grade: '9-12' },
  ] as const, [t]);

  const [filter, setFilter] = useState<string>('all');
  const f = FILTERS.find((x) => x.key === filter)!;
  const state = useApi(
    () => library.list({
      category:   (f as { category?: string }).category,
      grade_band: (f as { grade?: string }).grade,
      limit:      24,
    }),
    [filter],
  );

  const items: LibraryBook[] = state.status === 'success' ? state.data.items : [];

  /**
   * Deterministic Bento size from book.id — same book always gets the same tile.
   * Distribution target: ~10% wide / ~10% tall / ~60% square / ~20% mini.
   * Index 0 is always 'feature' (regardless of id).
   */
  function tileSize(bookId: string, index: number): 'feature' | 'wide' | 'tall' | 'square' | 'mini' {
    if (index === 0) return 'feature';
    let h = 5381;
    for (let i = 0; i < bookId.length; i++) h = ((h << 5) + h + bookId.charCodeAt(i)) | 0;
    const bucket = Math.abs(h) % 10;
    if (bucket === 0)        return 'wide';
    if (bucket === 1)        return 'tall';
    if (bucket >= 8)         return 'mini';
    return 'square';
  }

  return (
    <div className="container">
      <section className="lib-hero">
        <div className="kicker lib-hero-kicker">{t('library.kicker')}</div>
        <h1 className="h-display">
          {t('library.heroLine1')}<br />
          <em>{t('library.heroLine2')}</em>
        </h1>
        <p className="lib-sub">{t('library.heroSub')}</p>

        <div className="filter-rail">
          {FILTERS.map((opt) => (
            <button
              key={opt.key}
              className={`filter-item ${filter === opt.key ? 'active' : ''}`}
              onClick={() => setFilter(opt.key)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="sec-head">
          <h2>{t('library.featured')}</h2>
          <span className="meta">
            {state.status === 'loading' ? t('common.loading') :
             state.status === 'error'   ? t('common.offline') :
             `${t('library.curatedCount')} · ${String(state.data.total).padStart(2, '0')} vols`}
          </span>
        </div>

        {state.status === 'loading' && (
          <div className="book-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="book-card">
                <div className="cover-wrap"><div className="book-cover skeleton" /></div>
                <div className="shelf" />
                <div className="skeleton book-card-skel-title" />
                <div className="skeleton book-card-skel-author" />
              </div>
            ))}
          </div>
        )}

        {state.status === 'error' && (
          <p className="muted lib-state-row">
            {t('common.offline')} — <code>make api</code>
          </p>
        )}

        {state.status === 'success' && items.length === 0 && (
          <OnboardingEmptyState signedIn={Boolean(user)} authLoading={authLoading} />
        )}

        {state.status === 'success' && items.length > 0 && (
          <div className="book-grid book-grid--bento">
            {items.map((b, i) => {
              const size = tileSize(b.id, i);
              const isFeature = size === 'feature';
              const sizeClass = size === 'square' ? '' : ` book-card--${size}`;
              return (
                <Link
                  key={b.id}
                  to={`/books/${b.id}`}
                  className={`book-card${sizeClass}`}
                >
                  <div className="cover-wrap">
                    <div className="book-cover">
                      {COVER_BY_TITLE[b.title] ? (
                        <img src={COVER_BY_TITLE[b.title]} alt={`${b.title} cover`} />
                      ) : (
                        <div className="cover-placeholder">{b.title}</div>
                      )}
                    </div>
                    <div className="page-edge" />
                  </div>
                  <div className="shelf" />
                  <div className="book-meta-row">
                    <h3 className="book-title">{b.title}</h3>
                    <span className="book-num">№ {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="book-author">
                    {isFeature ? <em>{b.author ?? '—'}</em> : (b.author ?? '—')}
                  </div>
                  <div className="book-tags">
                    {b.curation?.tags?.slice(0, isFeature ? 2 : 1).map((tag) => (
                      <Badge key={tag} variant="soft" tone="neutral" size="sm">{tag}</Badge>
                    ))}
                    {b.curation?.age_recommended && (
                      <Badge variant="outline" tone="neutral" size="sm">
                        {b.curation.age_recommended}
                      </Badge>
                    )}
                    <Badge variant="soft" tone="accent" size="sm">{t('common.free')}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
